import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from 'react-query';

import Button from 'components/ui-kit/buttons/button';
import Spinner from 'components/ui-kit/spinner';
import * as Box from 'components/sent-request-confirmation-block/styles';
import { ReactComponent as Icon } from 'assets/icons/help-sent-icon.svg';

import { useApp } from 'hooks';
import useDepositOutcome from 'hooks/useDepositOutcome';

import { DEPOSIT_OUTCOME, forgetDepositReturn } from '../deposit';

import * as S from './styles';

const MESSAGES = {
  [DEPOSIT_OUTCOME.checking]: {
    title: 'Just a moment',
    hint: 'We are checking on your request.',
  },
  [DEPOSIT_OUTCOME.processing]: {
    title: 'Confirming your payment',
    hint: 'This can take a few minutes.',
  },
  [DEPOSIT_OUTCOME.confirming]: {
    title: 'We are confirming your payment',
    // Processing covers a payment left to a person as well: nothing here may promise it clears on its own.
    hint: 'The team will confirm your booking.',
  },
  [DEPOSIT_OUTCOME.unconfirmed]: {
    title: 'We couldn\'t confirm your payment',
    hint: 'If you paid, the team will be in touch to confirm your booking.',
  },
  [DEPOSIT_OUTCOME.booked]: {
    title: 'Your booking is confirmed',
    hint: 'Your deposit is paid.',
  },
  [DEPOSIT_OUTCOME.notPaid]: {
    title: 'Payment was not completed',
    hint: 'Your request is saved.',
  },
  [DEPOSIT_OUTCOME.refunded]: {
    title: 'Deposit refunded',
    hint: 'The money is on its way back to your card.',
  },
  [DEPOSIT_OUTCOME.replaced]: {
    title: 'This booking was changed',
    hint: 'Please use the newer one.',
  },
};

// The outcomes that are still on their way; the others are an answer.
const STILL_OPEN = [DEPOSIT_OUTCOME.checking, DEPOSIT_OUTCOME.processing];

// A reload must show the portal, not this screen and a second cancel.
const DECIDED = [
  DEPOSIT_OUTCOME.booked,
  DEPOSIT_OUTCOME.notPaid,
  DEPOSIT_OUTCOME.refunded,
  DEPOSIT_OUTCOME.replaced,
];

/**
 * What the booking window shows when the customer comes back from the payment page, in place of the form: nothing is
 * left to send again.
 */
const DepositReturn = ({ kind, token, onClose }) => {
  const { portal } = useApp();
  const queryClient = useQueryClient();
  const outcome = useDepositOutcome({ kind, token, portalId: portal.id });
  const { title, hint } = MESSAGES[outcome];

  // Once closed it is not shown again on a reload, whatever the payment came to.
  const close = () => {
    forgetDepositReturn(portal.id);
    onClose();
  };

  useEffect(() => {
    if (!DECIDED.includes(outcome)) return;

    forgetDepositReturn(portal.id);

    // The visit the booking made is on the customer's list now.
    if (outcome === DEPOSIT_OUTCOME.booked) queryClient.invalidateQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome]);

  return (
    <Box.ConfirmationBlock role="status">
      {STILL_OPEN.includes(outcome) && <S.Loading><Spinner /></S.Loading>}

      {/* The calendar with a check says a booking is made: no other answer may wear it. */}
      {outcome === DEPOSIT_OUTCOME.booked && (
        <Box.ImageContainer>
          <Box.Image>
            <Icon />
          </Box.Image>
        </Box.ImageContainer>
      )}

      <S.Title>{title}</S.Title>

      <S.Hint>{hint}</S.Hint>

      {!STILL_OPEN.includes(outcome) && (
        <Box.GotIt>
          <Button stretched color="primary" onClick={close}>Got it!</Button>
        </Box.GotIt>
      )}
    </Box.ConfirmationBlock>
  );
};

DepositReturn.propTypes = {
  kind: PropTypes.oneOf(['success', 'cancel']).isRequired,
  token: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DepositReturn;
